import {
  ScheduleComponent,
  Inject,
  ViewsDirective,
  ViewDirective,
  PopupOpenEventArgs,
  EventRenderedArgs,
  WorkWeek,
  NavigatingEventArgs,
  SelectEventArgs,
} from "@syncfusion/ej2-react-schedule";
import dayjs from "dayjs";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useDeviceType } from "../../../hooks/useDeviceType";
import { MiddleFontSize, SmallFontSize } from "../../../styles/font";
import { Tooltip } from "@mui/material";
import {
  DateInfo,
  MeetingRoomStatusInfo,
  OutlookDeepLinkParam,
  RoomColorInfo,
} from "../../../system/types/meetingRoom/MeetingRoom";

import { debounce } from "lodash";
import { isToday } from "../../../system/api/util/DateAndTimeUtil";
import { useMeetingRoom } from "../../../hooks/MeetingRoom/useMeetingRoom";
import { useRecoilValue } from "recoil";
import { MyDataAtom } from "../../../system/atoms";
import AlertModal from "../../Common/AlertModal";
import MeetingRoomEventModal from "../Modal/Event/MeetingRoomEventModal";

export interface NavigatingArgs {
  action: string;
  cancel?: boolean;
  currentDate?: string;
  name?: string;
  previousDate?: string;
}
type MeetingRoomWeekSchedularProps = {
  selectedMeetingRoom: MeetingRoomStatusInfo | null;
  dateInfo: DateInfo;
  handleChangeDateInfo: (valueObject: Partial<DateInfo>) => void;
  colorList: RoomColorInfo[];
  meetingRoomScheduleList: any;
  handleClickOpenOutlookPopup: (params: OutlookDeepLinkParam[]) => void;
  getMeetingRoomEvents: () => void;
};
const MeetingRoomWeekSchedular = memo(
  ({
    getMeetingRoomEvents,
    selectedMeetingRoom,
    dateInfo,
    handleChangeDateInfo,
    colorList,
    meetingRoomScheduleList,
    handleClickOpenOutlookPopup,
  }: MeetingRoomWeekSchedularProps) => {
    const scheduleObj = useRef<ScheduleComponent>(null);
    const deviceType = useDeviceType();
    const { email } = useRecoilValue(MyDataAtom);

    const [deleteModalInfo, setDeleteModalInfo] = useState({
      isOpen: false,
      selectedEvent: {},
    });

    const {
      checkRoomInAttendee,
      getMeetingRoomBgColor,
      getIsAvailableDateRange,
      checkIsOrganizer,
      checkPersonInAttendee,
    } = useMeetingRoom();
    // const { openOutlookPopup } = useOutlookDeeplink();
    // 팝업 이벤트 - 기존 popup cancel
    const handlePopupOpen = useCallback((args: PopupOpenEventArgs) => {
      args.cancel = true; // Cancel the default editor
    }, []);

    const onWorkCellRender = (args: any) => {
      const ignoredElementTypes = ["majorSlot", "minorSlot", "emptyCells"];

      if (
        !ignoredElementTypes.includes(args.elementType) &&
        isToday(args.date)
      ) {
        args.element.classList.add("custom-today-cell");
      }
    };

    // event의 style적용
    const applyEventStyle = useCallback(
      (args: EventRenderedArgs) => {
        const location = args.data.location;
        const attendees = args.data.attendees.map((el: any) => ({
          ...el,
          email: el.email.toLowerCase(),
        }));

        const isAttendee = checkRoomInAttendee(
          attendees,
          selectedMeetingRoom?.roomInfo
        );

        const isPersonInAttendee = checkPersonInAttendee(attendees, email);

        const findBgColor = getMeetingRoomBgColor(
          location,
          isAttendee,
          selectedMeetingRoom,
          colorList
        );
        // 배경색 설정
        args.element.style.backgroundColor = findBgColor;

        // 텍스트 스타일
        // args.element.style.height = "auto";
        args.element.style.color = "inherit";
        args.element.style.boxSizing = "border-box";
        args.element.style.border = "none";
        args.element.style.padding = "0px 2px";
        args.element.style.fontSize = `${
          deviceType === "mobile" ? SmallFontSize : MiddleFontSize
        }`;

        args.element.style.borderRadius = "4px";
        args.element.style.borderBottom = "1px solid #fff";
        args.element.style.cursor = "pointer";
        args.element.classList.add("custom-event");
        if (isPersonInAttendee) {
          args.element.classList.add("has-border");
          // args.element.style.position = "relative";

          // args.element.style.borderLeft = "4px solid #B23448"; // Brand500
        }
      },
      [
        colorList,
        deviceType,
        checkRoomInAttendee,
        checkPersonInAttendee,
        email,
        selectedMeetingRoom,
        getMeetingRoomBgColor,
      ]
    );

    // event가 렌더될 때 applyEventStyle을 적용.
    const onEventRendered = useCallback(
      (args: EventRenderedArgs) => {
        if (scheduleObj.current) {
          applyEventStyle(args);
        }
      },
      [applyEventStyle]
    );

    // 날짜 변경시 동작함수
    const handleViewNavigate = useCallback(
      (args: NavigatingEventArgs) => {
        const { currentDate, previousDate } = args;
        const current = dayjs(currentDate).format("YYYY-MM-DDTHH:mm");

        handleChangeDateInfo({
          stdDate: current,
        });
      },
      [handleChangeDateInfo]
    );

    const handleClickDeleteEvent = useCallback((e) => {
      e.originalEvent.stopPropagation();
      // e.stopPropagation();
      // const isOrganizer = checkIsOrganizer(event, email);

      const event = e.event;

      setDeleteModalInfo({
        isOpen: true,
        selectedEvent: event,
      });
    }, []);

    // 커스텀 이벤트 템플릿
    const eventTemplate = useCallback(
      (props: any) => {
        return (
          <Tooltip
            title={
              <div>
                <div>{props.Subject}</div>
                <div>
                  {`${dayjs(props.StartTime).format("HH:mm")} ~ ${dayjs(
                    props.EndTime
                  ).format("HH:mm")}`}
                </div>
                <div>{props.location}</div>
              </div>
            }
            placement="right" // 기본 위치지만 flip 옵션에 따라 자동 변경됨
          >
            <div
              style={{
                boxSizing: "border-box",
                width: "100%",
                height: "100%",
                cursor: "pointer",
                background: getMeetingRoomBgColor(
                  props.location,
                  checkRoomInAttendee(
                    props.attendees,
                    selectedMeetingRoom?.roomInfo
                  ),
                  selectedMeetingRoom,
                  colorList
                ),
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: SmallFontSize,
                    fontWeight: 600,
                    // 줄바꿈 처리
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textOverflow: "ellipsis",
                  }}
                >
                  {props.Subject}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: SmallFontSize,

                  // 줄바꿈 처리
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {`${dayjs(props.StartTime).format("HH:mm")} ~ ${dayjs(
                  props.EndTime
                ).format("HH:mm")}`}
              </div>
            </div>
          </Tooltip>
        );
      },
      [
        colorList,
        selectedMeetingRoom,
        checkRoomInAttendee,
        getMeetingRoomBgColor,
      ]
    );

    const debounceFn = useMemo(() => {
      return debounce((args: SelectEventArgs) => {
        if (!args.data) return; // data 없으면 무시
        if (Array.isArray(args.data)) return;

        const start = dayjs(args.data?.StartTime);
        const end = dayjs(args.data?.EndTime);

        const isAvailable = getIsAvailableDateRange(
          meetingRoomScheduleList,
          start,
          end
        );

        if (isAvailable) {
          if (end.diff(start, "minute") > 0) {
            const params = [
              { key: "startdt", value: start.format("YYYY-MM-DDTHH:mm:ss") },
              { key: "enddt", value: end.format("YYYY-MM-DDTHH:mm:ss") },
            ];

            if (selectedMeetingRoom) {
              const { roomInfo } = selectedMeetingRoom;
              params.push({
                key: "cc",
                value: `${roomInfo.displayName} <${roomInfo.mail}>`,
              });
            }

            handleClickOpenOutlookPopup(params);
          }
        } else {
          AlertModal("check", "선택한 시간에 이미 등록된 예약이 있습니다.");
        }
      }, 200);
    }, [
      getIsAvailableDateRange,
      handleClickOpenOutlookPopup,
      selectedMeetingRoom,
      meetingRoomScheduleList,
    ]);

    const handleCellSelect = useCallback(
      (args: SelectEventArgs) => {
        const target = (args as any).event?.target as HTMLElement;

        // 이벤트 위를 클릭한 경우는 무시 (이벤트는 .e-appointment 클래스가 있음)
        if (target?.closest(".e-appointment")) return;

        debounceFn(args);
      },
      [debounceFn]
    );

    const handleIsOpen = useCallback((status: boolean) => {
      setDeleteModalInfo((prev) => ({
        ...prev,
        selectedEvent: status === false ? {} : prev.selectedEvent,
        isOpen: status,
      }));
    }, []);

    return (
      <>
        <ScheduleComponent
          ref={scheduleObj}
          width="100%"
          delayUpdate={true}
          currentView="WorkWeek"
          selectedDate={new Date(dateInfo.stdDate)}
          eventSettings={{
            dataSource: meetingRoomScheduleList,
            template: eventTemplate as any, // 상위 버전에서는 Function이 포함되지만, 하위버전에서는 stinrg | undefined라 Element로 작성된 eventTemplate의 타입을 any로 지정.
          }}
          height={
            deviceType === "mobile"
              ? "calc(100dvh - 128px - 64px - 66px - 66px)"
              : "calc(100dvh - 128px - 16px - 66px)"
          }
          navigating={handleViewNavigate}
          startHour="08:00"
          endHour="20:00"
          popupOpen={handlePopupOpen}
          eventRendered={onEventRendered}
          eventClick={(e) => handleClickDeleteEvent(e)}
          renderCell={onWorkCellRender}
          locale="ko"
          rowAutoHeight={true}
          className="week-schedule-calendar"
          allowDragAndDrop={false}
          select={handleCellSelect}
          style={{
            fontSize: deviceType === "mobile" ? "10px" : "inherit",
          }}
        >
          <ViewsDirective>
            <ViewDirective option="WorkWeek" />
          </ViewsDirective>
          <Inject services={[WorkWeek]} />
        </ScheduleComponent>
        <MeetingRoomEventModal
          modalInfo={deleteModalInfo}
          handleIsOpen={handleIsOpen}
          refetchEventList={getMeetingRoomEvents}
        />
      </>
    );
  }
);

export default MeetingRoomWeekSchedular;
